// 板発注タブで選んだコマの保持 (issue #10107)。
//
// 板発注で選んだコマを、 入札追加・入札履歴・約定履歴タブとの往復や再訪をまたいで
// 保持する (spec `common/shared-components.md` AC-SC-031 の選択状態保持の考え方)。
// 全体フィルタ (AC-BIN-102) とは別の key で持つ。 板発注固有の選択は全体フィルタに
// 反映しない (AC-BIN-103 の板発注の絞り込みと同じ扱い)。
//
// 保持するのは「受渡日とコマの組」。 次の時は保持したコマを使わず、 入札できる先頭のコマ
// (GC 前の先頭。 issue #11101) にする:
// - 保持した受渡日と表示する受渡日が違う (受渡日を変えた / 「当日」 のまま翌日に再訪した)
// - 保持したコマが、 タブに戻った・開き直した時点で GC 済 (表示中に GC 済になっても
//   自動では移さない。 GC 済コマを自分で選んで約定を見る操作は残す)
import { formatDateValue } from '~/models/dateTime'
import { firstOpenSlot } from '~/pages/IntradayBidding/models/deliveryDate'
import { marketKomaRibbonState } from '~/pages/IntradayBidding/models/marketKomaRibbonState'

/** 板発注で選んだコマ。 `deliveryDate` は受渡日のカレンダー日 `YYYY-MM-DD`。 */
export type MarketBoardKomaSelection = {
  deliveryDate: string
  koma: number
}

/** localStorage へ載せる文字列にする。 */
export function serializeMarketBoardKoma(
  selection: MarketBoardKomaSelection,
): string {
  return JSON.stringify(selection)
}

/**
 * 受渡日で入札できる先頭のコマ (板発注を開いた時の既定のコマ)。 GC は受渡コマ開始の
 * 1 時間前なので、 当日は今のコマ + 3 (17:25 なら今のコマ 35 の 3 つ先のコマ 38)。
 * 入札できるコマが無い時 (当日の全コマが GC 済の深夜帯) は、 板発注の design
 * (`design/bidding/intraday-price.html` の初期選択) に合わせてコマ 1 にする。
 */
function firstOpenMarketBoardKoma(
  deliveryDate: Date,
  now: Date,
): MarketBoardKomaSelection {
  return {
    deliveryDate: formatDateValue(deliveryDate),
    koma: firstOpenSlot(now, deliveryDate) ?? 1,
  }
}

/**
 * localStorage の生文字列から、 板発注を開いた時のコマを決める。 壊れた値・範囲外の
 * コマは黙って入札できる先頭のコマへ落とす (`deserializeIntradayGlobalFilters` と同方針)。
 */
export function restoreMarketBoardKoma(
  raw: string | undefined,
  deliveryDate: Date,
  now: Date,
): MarketBoardKomaSelection {
  const fallback = firstOpenMarketBoardKoma(deliveryDate, now)
  if (raw == null) return fallback

  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return fallback
  }
  if (typeof parsed !== 'object' || parsed === null) return fallback
  const p = parsed as Record<string, unknown>

  if (p.deliveryDate !== fallback.deliveryDate) return fallback
  const koma = p.koma
  if (
    typeof koma !== 'number' ||
    !Number.isInteger(koma) ||
    koma < 1 ||
    koma > 48
  ) {
    return fallback
  }
  if (marketKomaRibbonState(now, deliveryDate, koma) === 'closed') {
    return fallback
  }
  return { deliveryDate: fallback.deliveryDate, koma }
}
